/*
 * Copyright (C) 2013 Spanning Cloud Apps.  All rights reserved.
 */
import './progress.tpl.html';
import './progressmobile.tpl.html';

/**
 * Module ties to the admin view of progress for actions like backup and export.
 */
angular
  .module('shared.progress', ['http.batch', 'shared.confirmModal', 'sbsf.enums'])
  /**
   * Progress controller.  Controller for the progress.tpl.html template.
   */
  .controller('SharedProgressCtrl', [
    '$rootScope',
    '$scope',
    '$filter',
    'progressFactory',
    'httpBatch',
    'confirmModalUtils',
    'PROGRESS_STATUS',
    'PROGRESS_ACTIONS',
    'PROGRESS_BOOKMARK_OBJECT',
    'BATCH_CANCELLATION_AVAILABILITY_CODE',
    function (
      $rootScope,
      $scope,
      $filter,
      progressFactory,
      httpBatch,
      confirmModalUtils,
      PROGRESS_STATUS,
      PROGRESS_ACTIONS,
      PROGRESS_BOOKMARK_OBJECT,
      BATCH_CANCELLATION_AVAILABILITY_CODE
    ) {
      $scope.ACTIONS = PROGRESS_ACTIONS;
      $scope.BOOKMARK_OBJECT = PROGRESS_BOOKMARK_OBJECT;
      $scope.showMinimize = angular.isDefined($scope.$parent.showMinimize) ? $scope.$parent.showMinimize : true;
      $scope.hideBackupProgress = $scope.hideBackupProgress || false;
      $scope.hideExportProgress = $scope.hideExportProgress || false;
      $scope.hideBulkRestoreProgress = $scope.hideBulkRestoreProgress || false;
      $scope.hideBulkRestorePreviewProgress = $scope.hideBulkRestorePreviewProgress || false;
      $scope.hideBulkRestoreCsvVerificationProgress = $scope.hideBulkRestoreCsvVerificationProgress || false;
      $scope.hideMetadataCompareProgress = $scope.hideMetadataCompareProgress || false;
      $scope.hideMetadataRestoreProgress = $scope.hideMetadataRestoreProgress || false;
      $scope.hideGdprRequestProgress = $scope.hideGdprRequestProgress || false;

      const i18n = $filter('i18n');

      const inProgressMap = new Map();

      /**
       * Utility for getting the current action progress object for the passed type.
       *
       * @param {number} type A type as defined in the ACTIONS constant.
       * @returns {Object} The progress for the requested type.
       */
      function getTypeProgress(type) {
        var retType;
        if (type === $scope.ACTIONS.BACKUP) {
          retType = $scope.backupProgress;
        } else if (type === $scope.ACTIONS.EXPORT) {
          retType = $scope.exportProgress;
        } else if (type === $scope.ACTIONS.BULK_RESTORE) {
          retType = $scope.bulkRestoreProgress;
        } else if (type === $scope.ACTIONS.BULK_RESTORE_PREVIEW) {
          retType = $scope.bulkRestorePreviewProgress;
        } else if (type === $scope.ACTIONS.BULK_RESTORE_CSV_VERIFICATION) {
          retType = $scope.bulkRestoreCsvVerificationProgress;
        } else if (type === $scope.ACTIONS.METADATA_COMPARE) {
          retType = $scope.metadataCompareProgress;
        } else if (type === $scope.ACTIONS.METADATA_RESTORE) {
          retType = $scope.metadataRestoreProgress;
        } else if (type === $scope.ACTIONS.GDPR) {
          retType = $scope.gdprRequestProgress;
        }

        return retType;
      }

      /**
       * Utility for getting the current action running text key.
       *
       * @param {number} type - A type as defined in the ACTIONS constant.
       * @returns {Object} - The text resource for the 'running' text for the type.
       */
      function getRunningTextKey(type) {
        var textResource;
        if (type === $scope.ACTIONS.BACKUP) {
          textResource = 'RUNNING_BACKUP';
        } else if (type === $scope.ACTIONS.EXPORT) {
          textResource = 'RUNNING_EXPORT';
        } else if (type === $scope.ACTIONS.BULK_RESTORE) {
          textResource = 'RUNNING_BULK_RESTORE';
        } else if (type === $scope.ACTIONS.BULK_RESTORE_PREVIEW) {
          textResource = 'RUNNING_BULK_RESTORE_PREVIEW';
        } else if (type === $scope.ACTIONS.BULK_RESTORE_CSV_VERIFICATION) {
          textResource = 'RUNNING_BULK_RESTORE_CSV_VERIFICATION';
        } else if (type === $scope.ACTIONS.METADATA_COMPARE) {
          textResource = 'RUNNING_METADATA_COMPARE';
        } else if (type === $scope.ACTIONS.METADATA_RESTORE) {
          textResource = 'RUNNING_METADATA_RESTORE';
        } else if (type === $scope.ACTIONS.GDPR) {
          textResource = 'RUNNING_GDPR_REQUEST';
        }

        return textResource;
      }

      /**
       * Utility for determine if the bookmark defines a percentComplete property to display progress bar
       * percentage, or if we have to compute it using objectsComplete and totalObjects.
       *
       * @param {Number} type - A type as defined in the ACTIONS constant.
       * @returns {Boolean} - True if we should inspect percentComplete, false otherwise.
       */
      function usesPercentComplete(type) {
        return (
          type === $scope.ACTIONS.EXPORT ||
          type === $scope.ACTIONS.BULK_RESTORE ||
          type === $scope.ACTIONS.BULK_RESTORE_PREVIEW ||
          type === $scope.ACTIONS.METADATA_COMPARE ||
          type === $scope.ACTIONS.METADATA_RESTORE ||
          type === $scope.ACTIONS.GDPR
        );
      }

      /**
       * Function used to get the percent done for an action type.
       *
       * @param {int} type The type of interest.
       * @returns {{width: string}} The style expression required by the template specifying the width.
       */
      $scope.getPercentDone = function (type) {
        var bookmark, pct;
        bookmark = getTypeProgress(type).bookmark;

        // some batch types report percentage directly in the bookmark to allow for a more granular representation
        if (usesPercentComplete(type)) {
          pct = bookmark && bookmark.percentComplete > 0 ? parseInt(bookmark.percentComplete, 10) : 0;
        } else {
          pct =
            bookmark && bookmark.totalObjects > 0
              ? parseInt((bookmark.objectsComplete / bookmark.totalObjects) * 100, 10)
              : 0;
          // Make sure that we will not display progress more than 99%
          pct = Math.min(pct, 99);
        }

        return { width: pct + '%' };
      };

      /**
       * Function used to get the text resource object key for a progress action and bookmark.
       *
       * @param {int} action - The action of the progress.
       * @param {Object} bookmark - bookmark object of progress.
       * @returns {String} Object Key for text resource.
       */
      $scope.getProgressObjKey = function (action, bookmark) {
        var objectType = getCurrentObjectType(bookmark);
        var currBookmarkObj = objectType ? $scope.BOOKMARK_OBJECT[objectType.toUpperCase()] : null,
          bkmrkObj = $scope.BOOKMARK_OBJECT,
          objKey;
        if (action === $scope.ACTIONS.BACKUP) {
          objKey = 'BACKING_UP';
        } else if (action === $scope.ACTIONS.BULK_RESTORE) {
          if (currBookmarkObj === bkmrkObj.LOADING) {
            objKey = 'RETRIEVING_BULK_RESTORE';
          } else {
            objKey = 'BULK_RESTORE_RUNNING';
          }
        } else if (action === $scope.ACTIONS.BULK_RESTORE_CSV_VERIFICATION) {
          objKey = 'VERIFYING';
        } else if (action === $scope.ACTIONS.METADATA_COMPARE) {
          if (currBookmarkObj === bkmrkObj.LOADING) {
            objKey = 'RETRIEVING_METADATA';
          } else {
            objKey = 'COMPARING_METADATA';
          }
        } else if (action === $scope.ACTIONS.METADATA_RESTORE) {
          if (currBookmarkObj === bkmrkObj.LOADING) {
            objKey = 'RETRIEVING_METADATA_FOR_RESTORE';
          } else {
            objKey = 'RESTORING_METADATA';
          }
        } else if (action === $scope.ACTIONS.GDPR) {
          if (currBookmarkObj === bkmrkObj.LOADING) {
            objKey = 'RETRIEVING_RECORDS_TO_HANDLE';
          } else {
            objKey = 'HANDLE_RECORDS';
          }
        } else {
          if (
            currBookmarkObj === bkmrkObj.COMPRESSING ||
            currBookmarkObj === bkmrkObj.UPLOADING ||
            currBookmarkObj === bkmrkObj.DOWNLOADING
          ) {
            objKey =
              action === $scope.ACTIONS.BULK_RESTORE_PREVIEW ? 'BULK_RESTORE_PREVIEW_PROGRESS' : 'EXPORT_PROGRESS';
          } else {
            objKey = 'EXPORTING';
          }
        }
        return objKey;
      };

      /**
       * Get the progress message for a type. It will return a queued message if the action type is in a queued
       * state.
       *
       * @param {int} type The type of interest.
       * @returns {String} The message.
       */
      $scope.getProgressMessage = function (type) {
        var progress = getTypeProgress(type),
          bookmark = progress.bookmark,
          typeMsg = i18n($scope.ACTIONS[progress.action]),
          objKey = $scope.getProgressObjKey(progress.action, bookmark),
          objType,
          textResource,
          msg;

        if (progress.cancelBatch) {
          msg = i18n('CANCELLING_MSG', [$scope.ACTIONS[progress.action]]);
        } else if (getTypeProgress(type).status === PROGRESS_STATUS.QUEUED) {
          msg = i18n('QUEUED_MSG', [typeMsg]);
        } else {
          if (bookmark) {
            objType = getCurrentObjectType(bookmark);
            if (!objType || objType === ' ') {
              msg = i18n('LOADING_MSG');
            } else {
              msg = i18n(objKey, objType);
            }
          } else {
            textResource = getRunningTextKey(type);
            msg = i18n(textResource);
          }
        }
        return msg;
      };

      /**
       * Get the current item count message from an action type.
       *
       * @param {int} type The type of interest.
       * @returns {String} The message specifying the current object count.
       */
      $scope.getCurrentItemCount = function (type) {
        var bookmark = getTypeProgress(type).bookmark,
          currentObjectType = getCurrentObjectType(bookmark),
          currentObjectCounts,
          progressMessage,
          totalObjects,
          recordText;

        if (!bookmark || (bookmark && !bookmark.objects && !bookmark.items)) {
          return '';
        }

        if (type === $scope.ACTIONS.BULK_RESTORE) {
          if (!bookmark.totalObjects) {
            return '';
          }

          recordText = bookmark.totalObjects === 1 ? i18n('RECORD') : i18n('RECORDS');
          totalObjects = bookmark.totalObjects + (bookmark.totalChildren || 0);

          // This assumes the bulk restore progress will be like object mentioned above.
          progressMessage = i18n('BULK_RESTORE_PROGRESS_MSG', [bookmark.totalObjects, recordText]);

          // If the number of items is equal to the total items (there are no children records), dont show total items text.
          if (bookmark.totalObjects !== totalObjects) {
            progressMessage = progressMessage + i18n('BULK_RESTORE_PROGRESS_MSG_TOTAL_ITEMS', [totalObjects]);
          }
        } else {
          currentObjectCounts = bookmark.objects[currentObjectType];
          if (angular.isDefined(currentObjectCounts)) {
            progressMessage = i18n('PROGRESS_MSG', [
              currentObjectCounts['new'] + currentObjectCounts.updated,
              currentObjectType
            ]);
          } else {
            progressMessage = '';
          }
        }

        return progressMessage;
      };

      /**
       * Get the css class that should be applied to a progress bar based on state of an action.
       *
       * @param {int} type The type of interest.
       * @returns {string} One of more css classes to apply to the progress bar.
       */
      $scope.getProgressCls = function (type) {
        var bookmark = getTypeProgress(type).bookmark;
        var objectType = getCurrentObjectType(bookmark);
        return objectType ? 'sbsf-progress-running' : 'progress-striped active';
      };

      /**
       * Used to get the current minimized setting for the progress view.
       *
       * @returns {boolean} - True if minimized.
       */
      $scope.getMinimized = function () {
        return progressFactory.minimized;
      };

      /**
       * Toggle the minimized value.
       */
      $scope.toggleMinimized = function () {
        progressFactory.minimized = !progressFactory.minimized;
      };

      /**
       * Determines if the progress div needs to be shown or not.
       *
       * @returns {Boolean} - True if a batch (of any type) is in progress.
       */
      $scope.showProgressBars = function () {
        return (
          $rootScope.backupInProgress ||
          $rootScope.exportInProgress ||
          $rootScope.bulkRestoreInProgress ||
          $rootScope.bulkRestorePreviewInProgress ||
          $rootScope.bulkRestoreCsvVerificationInProgress ||
          $rootScope.metadataCompareInProgress ||
          $rootScope.metadataRestoreInProgress ||
          $rootScope.gdprOperationInProgress
        );
      };

      /**
       * Determines whether the cancellation button should be displayed or not
       *
       * @param {int} type - The type of interest.
       * @returns {boolean} - should the cancel button be displayed or not
       */
      $scope.showCancellationButton = function (type) {
        const progress = getTypeProgress(type);
        if (!progress || !progress.cancellationAvailability) {
          return false;
        }
        return (
          progress.cancellationAvailability.code !== BATCH_CANCELLATION_AVAILABILITY_CODE.ProgressActionNotSupported
        );
      };

      /**
       * Determines whether the cancellation is available or not
       *
       * @param {int} type - The type of interest.
       * @returns {boolean} - should the cancel button be disabled or not
       */
      $scope.isCancellationAvailable = function (type) {
        const progress = getTypeProgress(type);
        return progress.cancellationAvailability.isAvailable && !inProgressMap.get(progress.id);
      };

      /**
       * Get the cancellation description
       *
       * @param {int} type - The type of interest.
       * @returns {boolean} - cancellation description
       */
      $scope.getCancellationDescription = function (type) {
        const progress = getTypeProgress(type);
        let result;
        const actionName = i18n($scope.ACTIONS[progress.action]);
        switch (progress.cancellationAvailability.code) {
          case BATCH_CANCELLATION_AVAILABILITY_CODE.CancellationHaveAlreadyBeenRequested:
            result = i18n('STOPPING_MSG', [actionName]);
            break;
          case BATCH_CANCELLATION_AVAILABILITY_CODE.HasChildProcesses:
            result = i18n('STOPPING_NOT_AVAILABLE');
            break;
          case BATCH_CANCELLATION_AVAILABILITY_CODE.BatchNotExist:
            result = i18n('WAIT_TO_START_MSG', [actionName]);
            break;
          case BATCH_CANCELLATION_AVAILABILITY_CODE.DoNotHavePermission:
            result = i18n('PERMISSION_MISSED');
            break;
          case BATCH_CANCELLATION_AVAILABILITY_CODE.Available:
            result = i18n('CLICK_TO_STOP', [actionName]);
            break;
          default:
            result = '';
            break;
        }
        return result;
      };

      /**
       * Send the cancellation request
       *
       * @param {int} type - The type of interest.
       */
      $scope.sendCancellationRequest = function (type) {
        const isAvailable = $scope.isCancellationAvailable(type);
        if (!isAvailable) {
          return;
        }
        confirmModalUtils
          .openConfirmModal({
            titleTextKey: 'CONTINUE',
            confirmTextKey: 'ARE_SURE_STOP'
          })
          .then((isConfirmed) => {
            if (!isConfirmed) {
              return;
            }
            const progress = getTypeProgress(type);
            inProgressMap.set(progress.id, true);
            httpBatch.postBatchCancellation(progress.bookmark.batchId).then(() => {
              progress.cancellationAvailability.isAvailable = false;
              progress.cancellationAvailability.code =
                BATCH_CANCELLATION_AVAILABILITY_CODE.CancellationHaveAlreadyBeenRequested;
              inProgressMap.delete(progress.id);
            });
          });
      };

      /**
       * Determines the current running object type in a bookmark.
       *
       * @param {Object} bookmark - the bookmark to be checked
       * @returns {String} - the object type
       */
      function getCurrentObjectType(bookmark) {
        var objectType;
        if (bookmark) {
          if (_.has(bookmark, 'runningObjectTypes')) {
            objectType = _.last(bookmark.runningObjectTypes);
          } else {
            objectType = bookmark.currentObject;
          }
        }
        return objectType;
      }
    }
  ]);
